import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { USERS_API_URL } from '../../config/apiConfig';
import UploadOverlay from '../UploadOverlay/UploadOverlay';
import './UserManagementScreen.css';

const UserManagementScreen = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showRoleForm, setShowRoleForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedUserForRoles, setSelectedUserForRoles] = useState(null);
  const [userRoles, setUserRoles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [tierChangeNote, setTierChangeNote] = useState('');
  const [billingExempt, setBillingExempt] = useState(false);
  const [industryVip, setIndustryVip] = useState(false);
  const [passwordNeverExpires, setPasswordNeverExpires] = useState(false);
  const [emailDigestFrequency, setEmailDigestFrequency] = useState('none');
  const [adminLandingPage, setAdminLandingPage] = useState('/amplifier_search');
  const [watchedItems, setWatchedItems] = useState([]);
  const [watchedItemsLoading, setWatchedItemsLoading] = useState(false);
  const [watchedItemsError, setWatchedItemsError] = useState('');
  
  const { token } = useAuth();

  // Form state
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    nickname: '',
    password: '',
    selectedRoles: []
  });

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${USERS_API_URL}/users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      } else {
        throw new Error('Failed to fetch users');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await fetch(`${USERS_API_URL}/roles`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setRoles(data);
      } else {
        throw new Error('Failed to fetch roles');
      }
    } catch (err) {
      console.error('Error fetching roles:', err);
    }
  };

  const fetchUserRoles = async (userId) => {
    try {
      const response = await fetch(`${USERS_API_URL}/users/${userId}/roles`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUserRoles(data);
      } else {
        throw new Error('Failed to fetch user roles');
      }
    } catch (err) {
      setError('Error fetching user roles: ' + err.message);
    }
  };

  const handleManageRoles = async (user) => {
    setSelectedUserForRoles(user);
    await fetchUserRoles(user.user_id);
    setShowRoleForm(true);
  };

  const handleAssignRole = async (roleId) => {
    try {
      const response = await fetch(`${USERS_API_URL}/users/${selectedUserForRoles.user_id}/roles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ role_id: roleId })
      });

      if (response.ok) {
        await fetchUserRoles(selectedUserForRoles.user_id);
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Failed to assign role');
      }
    } catch (err) {
      setError('Error assigning role: ' + err.message);
    }
  };

  const handleRemoveRole = async (roleId) => {
    try {
      const response = await fetch(`${USERS_API_URL}/users/${selectedUserForRoles.user_id}/roles/${roleId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        await fetchUserRoles(selectedUserForRoles.user_id);
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Failed to remove role');
      }
    } catch (err) {
      setError('Error removing role: ' + err.message);
    }
  };

  const handleCreateUser = () => {
    setEditingUser(null);
    setFormData({
      username: '',
      email: '',
      nickname: '',
      password: '',
      selectedRoles: []
    });
    setPhotoPreview(null);
    setPhotoFile(null);
    setShowForm(true);
  };

  const handleEditUser = async (user) => {
    setEditingUser(user);
    setBillingExempt(user.is_billing_exempt || false);
    setIndustryVip(user.is_industry_vip || false);
    setPasswordNeverExpires(user.password_never_expires || false);
    setEmailDigestFrequency(user.email_digest_frequency || 'none');
    setAdminLandingPage(user.landing_page || '/amplifier_search');
    setTierChangeNote('');
    
    // Fetch user's current roles
    try {
      const response = await fetch(`${USERS_API_URL}/users/${user.user_id}/roles`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      let currentRoles = [];
      if (response.ok) {
        const rolesData = await response.json();
        currentRoles = rolesData.map(role => role.role_id);
      }

      setFormData({
        username: user.username,
        email: user.email,
        nickname: user.nickname || '',
        password: '',
        selectedRoles: currentRoles,
        alternate_email: user.alternate_email || '',
        phone: user.phone || ''
      });

      // Set photo preview if user has photo
      if (user.has_photo) {
        setPhotoPreview(`${USERS_API_URL}/users/${user.user_id}/photo`);
      } else {
        setPhotoPreview(null);
      }
      setPhotoFile(null);
    } catch (err) {
      console.error('Error fetching user roles:', err);
      setFormData({
        username: user.username,
        email: user.email,
        nickname: user.nickname || '',
        password: '',
        selectedRoles: [],
        alternate_email: user.alternate_email || '',
        phone: user.phone || ''
      });
      setPhotoPreview(null);
      setPhotoFile(null);
    }
    
    // Fetch watched items for this user
    setWatchedItemsError('');
    setWatchedItems([]);
    setWatchedItemsLoading(true);
    try {
      const subsResponse = await fetch(`${USERS_API_URL}/users/${user.user_id}/subscriptions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (subsResponse.ok) {
        const subsData = await subsResponse.json();
        setWatchedItems(subsData.subscriptions || []);
      }
    } catch (err) {
      console.error('Error fetching watched items:', err);
    } finally {
      setWatchedItemsLoading(false);
    }

    setShowForm(true);
  };

  const handleRemoveWatch = async (subscriptionId) => {
    if (!editingUser) return;
    try {
      const response = await fetch(`${USERS_API_URL}/users/${editingUser.user_id}/subscriptions/${subscriptionId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setWatchedItems(prev => prev.filter(item => item.subscription_id !== subscriptionId));
      } else {
        const data = await response.json();
        setWatchedItemsError(data.error || 'Failed to remove watched item');
      }
    } catch (err) {
      setWatchedItemsError('Failed to remove watched item');
    }
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const url = editingUser 
        ? `${USERS_API_URL}/users/${editingUser.user_id}`
        : `${USERS_API_URL}/users`;
      
      const method = editingUser ? 'PUT' : 'POST';
      
      const body = editingUser 
        ? { username: formData.username, email: formData.email, nickname: formData.nickname,
            alternate_email: formData.alternate_email, phone: formData.phone,
            email_digest_frequency: emailDigestFrequency, landing_page: adminLandingPage,
            password_never_expires: passwordNeverExpires }
        : { username: formData.username, email: formData.email, nickname: formData.nickname, password: formData.password };

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        const userData = await response.json();
        const userId = editingUser ? editingUser.user_id : userData.user_id;

        // Handle role assignments
        if (editingUser) {
          // For editing: get current roles and update them
          const currentRolesResponse = await fetch(`${USERS_API_URL}/users/${userId}/roles`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          let currentRoleIds = [];
          if (currentRolesResponse.ok) {
            const currentRoles = await currentRolesResponse.json();
            currentRoleIds = currentRoles.map(role => role.role_id);
          }

          // Remove roles that are no longer selected
          for (const roleId of currentRoleIds) {
            if (!formData.selectedRoles.includes(roleId)) {
              await fetch(`${USERS_API_URL}/users/${userId}/roles/${roleId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
              });
            }
          }

          // Add new roles
          for (const roleId of formData.selectedRoles) {
            if (!currentRoleIds.includes(roleId)) {
              await fetch(`${USERS_API_URL}/users/${userId}/roles`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ role_id: roleId })
              });
            }
          }
        } else {
          // For new users: assign selected roles
          for (const roleId of formData.selectedRoles) {
            await fetch(`${USERS_API_URL}/users/${userId}/roles`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({ role_id: roleId })
            });
          }
        }

        // Upload photo if one was selected
        if (photoFile) {
          setUploadingPhoto(true);
          try {
            const formData = new FormData();
            formData.append('photo', photoFile);
            const photoResponse = await fetch(`${USERS_API_URL}/users/${userId}/photo`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`
                // Do NOT set Content-Type; browser will set it to multipart/form-data
              },
              body: formData
            });

            if (!photoResponse.ok) {
              console.error('Failed to upload photo');
            }
          } catch (photoErr) {
            console.error('Photo upload error:', photoErr);
          } finally {
            setUploadingPhoto(false);
          }
        }

        await fetchUsers();
        setShowForm(false);
        setEditingUser(null);
        setPhotoPreview(null);
        setPhotoFile(null);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Operation failed');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError('Photo must be less than 5MB');
        return;
      }
      setPhotoFile(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleDeletePhoto = async () => {
    if (!editingUser) return;
    
    if (window.confirm('Are you sure you want to delete this photo?')) {
      try {
        const response = await fetch(`${USERS_API_URL}/users/${editingUser.user_id}/photo`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          setPhotoPreview(null);
          setPhotoFile(null);
          await fetchUsers();
        } else {
          throw new Error('Failed to delete photo');
        }
      } catch (err) {
        setError('Error deleting photo: ' + err.message);
      }
    }
  };

  const handleDeleteUser = async (userId, username) => {
    if (window.confirm(`Are you sure you want to delete user "${username}"?`)) {
      try {
        const response = await fetch(`${USERS_API_URL}/users/${userId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          await fetchUsers();
        } else {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Delete failed');
        }
      } catch (err) {
        setError(err.message);
      }
    }
  };

  const handleToggleLock = async (userId, isActive, username) => {
    const action = isActive ? 'lock' : 'unlock';
    
    try {
      const response = await fetch(`${USERS_API_URL}/users/${userId}/${action}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        await fetchUsers();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || `${action} failed`);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleResetPassword = async (userId, username) => {
    const newPassword = prompt(`Enter new password for user "${username}":`);
    if (newPassword) {
      try {
        const response = await fetch(`${USERS_API_URL}/users/${userId}/reset-password`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ newPassword })
        });

        if (response.ok) {
          alert('Password reset successfully');
        } else {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Password reset failed');
        }
      } catch (err) {
        setError(err.message);
      }
    }
  };

  const handleChangeTier = async (userId, newTier, username, exempt) => {
    const currentUser = users.find(u => u.user_id === userId);
    const currentTier = currentUser?.subscription_tier || 'free';
    const currentExempt = currentUser?.is_billing_exempt || false;
    
    // Allow saving if tier changed OR billing exempt changed
    if (newTier === currentTier && exempt === currentExempt) return;

    const action = newTier === 'free' ? 'downgrade' : 'upgrade';
    const exemptLabel = exempt ? ' (Complimentary)' : '';
    if (!window.confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} ${username} from ${currentTier} to ${newTier}${exemptLabel}?`)) {
      return;
    }

    try {
      const response = await fetch(`${USERS_API_URL}/users/${userId}/subscription`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          subscription_tier: newTier, 
          is_billing_exempt: exempt,
          note: tierChangeNote || `Admin ${action}${exemptLabel} via User Management` 
        })
      });

      if (response.ok) {
        const data = await response.json();
        alert(`${data.message} for ${username}`);
        setTierChangeNote('');
        // Update editingUser so the dropdown reflects the new tier immediately
        if (editingUser && editingUser.user_id === userId) {
          setEditingUser(prev => ({
            ...prev,
            subscription_tier: data.subscription_tier,
            monthly_reply_count: data.monthly_reply_count,
            is_billing_exempt: data.is_billing_exempt
          }));
          setBillingExempt(data.is_billing_exempt);
        }
        await fetchUsers();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Tier change failed');
      }
    } catch (err) {
      setError('Error changing tier: ' + err.message);
    }
  };

  const handleToggleVIP = async (userId, newVip, username) => {
    const action = newVip ? 'grant' : 'revoke';
    if (!window.confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} Industry VIP badge for ${username}?`)) return;

    try {
      const response = await fetch(`${USERS_API_URL}/users/${userId}/industry-vip`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ is_industry_vip: newVip })
      });

      if (response.ok) {
        const data = await response.json();
        alert(data.message);
        if (editingUser && editingUser.user_id === userId) {
          setEditingUser(prev => ({ ...prev, is_industry_vip: data.is_industry_vip }));
          setIndustryVip(data.is_industry_vip);
        }
        await fetchUsers();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'VIP toggle failed');
      }
    } catch (err) {
      setError('Error updating Industry VIP: ' + err.message);
    }
  };

  const handleResendVerification = async (userId, email, username) => {
    if (!window.confirm(`Resend verification email to ${username} (${email})?`)) return;
    try {
      const response = await fetch(`${USERS_API_URL}/users/${userId}/resend-verification`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        alert(data.message);
      } else {
        throw new Error(data.error || 'Resend failed');
      }
    } catch (err) {
      setError('Error resending verification: ' + err.message);
    }
  };

  const filteredUsers = users.filter(user => 
    user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (user.nickname && user.nickname.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return <div className="loading">Loading users...</div>;
  }

  return (
    <div className="user-management-screen">
      <UploadOverlay
        visible={uploadingPhoto}
        message="Uploading photo…"
        subtext="Processing your upload"
      />
      <div className="header">
        <h1>User Management</h1>
        <button className="create-button" onClick={handleCreateUser}>
          Create New User
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="search-bar">
        <input
          type="text"
          placeholder="Search users..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="users-table">
        <table>
          <thead>
            <tr>
              <th style={{ width: '50px' }}>Photo</th>
              <th>Username</th>
              <th>Nickname</th>
              <th>Email</th>
              <th>Tier</th>
              <th>Admin</th>
              <th>Status</th>
              <th>Last Login</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map(user => (
              <tr key={user.user_id}>
                <td>
                  {user.has_photo ? (
                    <img 
                      src={`${USERS_API_URL}/users/${user.user_id}/photo`}
                      alt={user.username}
                      style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }}
                    />
                  ) : (
                    <div style={{ 
                      width: 40, 
                      height: 40, 
                      borderRadius: '50%', 
                      background: '#e0e0e0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '16px',
                      color: '#666'
                    }}>
                      {user.username.charAt(0).toUpperCase()}
                    </div>
                  )}
                </td>
                <td>{user.username}</td>
                <td>{user.nickname || '-'}</td>
                <td>
                  {user.email}
                  {user.email_status === 'disabled' && (
                    <span style={{
                      display: 'inline-block',
                      marginLeft: '6px',
                      padding: '1px 6px',
                      borderRadius: '4px',
                      fontSize: '10px',
                      fontWeight: '600',
                      background: '#fff5f5',
                      color: '#c53030',
                      border: '1px solid #fc8181'
                    }} title={user.email_status_reason || 'Email disabled'}>
                      DISABLED
                    </span>
                  )}
                  {user.email_verified ? (
                    <span style={{
                      display: 'inline-block',
                      marginLeft: '6px',
                      padding: '1px 6px',
                      borderRadius: '4px',
                      fontSize: '10px',
                      fontWeight: '600',
                      background: '#f0fff4',
                      color: '#276749',
                      border: '1px solid #68d391'
                    }} title="Email address verified">
                      VERIFIED
                    </span>
                  ) : (
                    <span style={{
                      display: 'inline-block',
                      marginLeft: '6px',
                      padding: '1px 6px',
                      borderRadius: '4px',
                      fontSize: '10px',
                      fontWeight: '600',
                      background: '#fffbeb',
                      color: '#92400e',
                      border: '1px solid #fcd34d'
                    }} title="Email address not yet verified">
                      UNVERIFIED
                    </span>
                  )}
                </td>
                <td>
                  <span style={{
                    display: 'inline-block',
                    padding: '2px 10px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '600',
                    background: user.subscription_tier === 'pro' ? '#eff6ff' 
                              : user.subscription_tier === 'business' ? '#f0fdf4' 
                              : '#f8f9fa',
                    color: user.subscription_tier === 'pro' ? '#2563eb' 
                         : user.subscription_tier === 'business' ? '#16a34a' 
                         : '#666',
                    border: `1px solid ${user.subscription_tier === 'pro' ? '#bfdbfe' 
                           : user.subscription_tier === 'business' ? '#bbf7d0' 
                           : '#e9ecef'}`,
                    textTransform: 'capitalize'
                  }}>
                    {user.subscription_tier || 'free'}
                    {user.is_billing_exempt && (
                      <span style={{ marginLeft: '4px', fontSize: '10px', opacity: 0.75 }} title="Complimentary — no billing">COMP</span>
                    )}
                  </span>
                  {user.is_industry_vip && (
                    <span style={{ display: 'inline-block', marginLeft: '6px', fontSize: '11px', fontWeight: '700', color: '#7c2d12', background: 'linear-gradient(135deg, #fef3c7, #fde68a)', border: '1px solid #f59e0b', borderRadius: '10px', padding: '1px 6px', whiteSpace: 'nowrap' }}>⭐ VIP</span>
                  )}
                </td>
                <td>{user.is_admin ? 'Yes' : 'No'}</td>
                <td>
                  <span className={`status ${user.is_active ? 'active' : 'locked'}`}>
                    {user.is_active ? 'Active' : 'Locked'}
                  </span>
                </td>
                <td>
                  {user.last_login ? new Date(user.last_login).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' }) : 'Never'}
                </td>
                <td className="actions">
                  <button 
                    className="edit-button"
                    onClick={() => handleEditUser(user)}
                  >
                    Edit
                  </button>
                  <button 
                    className="manage-roles-button"
                    onClick={() => handleManageRoles(user)}
                  >
                    Manage Roles
                  </button>
                  <button 
                    className={`lock-button ${user.is_active ? 'lock' : 'unlock'}`}
                    onClick={() => handleToggleLock(user.user_id, user.is_active, user.username)}
                  >
                    {user.is_active ? 'Lock' : 'Unlock'}
                  </button>
                  <button 
                    className="reset-password-button"
                    onClick={() => handleResetPassword(user.user_id, user.username)}
                  >
                    Reset Password
                  </button>
                  <button
                    onClick={() => handleResendVerification(user.user_id, user.email, user.username)}
                    style={{
                      padding: '4px 10px',
                      background: '#fffbeb',
                      color: '#92400e',
                      border: '1px solid #fcd34d',
                      borderRadius: '4px',
                      fontSize: '12px',
                      cursor: 'pointer',
                      fontWeight: '500'
                    }}
                  >
                    Resend Verify
                  </button>
                  <button 
                    className="delete-button"
                    onClick={() => handleDeleteUser(user.user_id, user.username)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>{editingUser ? 'Edit User' : 'Create User'}</h2>
              <button 
                className="close-button"
                onClick={() => {
                  setShowForm(false);
                  setPhotoPreview(null);
                  setPhotoFile(null);
                }}
              >
                ×
              </button>
            </div>
            
            <form onSubmit={handleSubmitForm}>
              {/* Photo Section */}
              <div className="form-group" style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                <label>Profile Photo</label>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
                  {photoPreview ? (
                    <img 
                      src={photoPreview}
                      alt="Preview"
                      style={{ 
                        width: 100, 
                        height: 100, 
                        borderRadius: '50%', 
                        objectFit: 'cover',
                        border: '3px solid #4CAF50'
                      }}
                    />
                  ) : (
                    <div style={{ 
                      width: 100, 
                      height: 100, 
                      borderRadius: '50%', 
                      background: '#e0e0e0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '32px',
                      color: '#666'
                    }}>
                      {formData.username ? formData.username.charAt(0).toUpperCase() : '?'}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      type="button"
                      className="photo-action-button"
                      onClick={() => document.getElementById('photo-upload').click()}
                      disabled={uploadingPhoto}
                    >
                      {uploadingPhoto ? 'Uploading...' : 'Choose Photo'}
                    </button>
                    <input
                      type="file"
                      id="photo-upload"
                      accept="image/*"
                      onChange={handlePhotoChange}
                      style={{ display: 'none' }}
                      disabled={uploadingPhoto}
                    />
                    {photoPreview && editingUser && (
                      <button
                        type="button"
                        onClick={handleDeletePhoto}
                        className="photo-action-button delete"
                      >
                        Delete Photo
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="username">Username</label>
                <input
                  type="text"
                  id="username"
                  value={formData.username}
                  onChange={(e) => setFormData({...formData, username: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="nickname">Nickname (Display Name)</label>
                <input
                  type="text"
                  id="nickname"
                  value={formData.nickname}
                  onChange={(e) => setFormData({...formData, nickname: e.target.value})}
                  placeholder="Optional display name"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                />
                {editingUser && editingUser.email_status === 'disabled' && (
                  <div style={{
                    marginTop: '6px',
                    padding: '8px 12px',
                    background: '#fff5f5',
                    border: '1px solid #fc8181',
                    borderRadius: '6px',
                    fontSize: '13px',
                    color: '#c53030'
                  }}>
                    <strong>⚠️ Email disabled:</strong> {editingUser.email_status_reason || 'Bounce or complaint detected'}
                    <br />
                    <button
                      type="button"
                      style={{
                        marginTop: '6px',
                        padding: '4px 12px',
                        fontSize: '12px',
                        background: '#48bb78',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                      onClick={async () => {
                        try {
                          const resp = await fetch(`${USERS_API_URL}/users/${editingUser.user_id}/email-status`, {
                            method: 'PATCH',
                            headers: {
                              'Content-Type': 'application/json',
                              'Authorization': `Bearer ${token}`
                            },
                            body: JSON.stringify({ email_status: 'active' })
                          });
                          if (resp.ok) {
                            setEditingUser({ ...editingUser, email_status: 'active', email_status_reason: null });
                            fetchUsers();
                          } else {
                            const err = await resp.json();
                            setError(err.error || 'Failed to re-enable email');
                          }
                        } catch (err) {
                          setError('Error re-enabling email: ' + err.message);
                        }
                      }}
                    >
                      Re-enable Email
                    </button>
                  </div>
                )}
              </div>

              {editingUser && (
                <>
                  <div className="form-group">
                    <label htmlFor="alternate_email">Alternate Email</label>
                    <input
                      type="email"
                      id="alternate_email"
                      value={formData.alternate_email || ''}
                      onChange={(e) => setFormData({...formData, alternate_email: e.target.value})}
                      placeholder="Backup email address"
                    />
                    <small style={{ color: '#718096', fontSize: '12px' }}>Used to contact user if primary email has issues</small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone">Phone</label>
                    <input
                      type="tel"
                      id="phone"
                      value={formData.phone || ''}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder="Optional phone number"
                    />
                  </div>
                </>
              )}
              
              {!editingUser && (
                <div className="form-group">
                  <label htmlFor="password">Password</label>
                  <input
                    type="password"
                    id="password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    required
                  />
                </div>
              )}
              
              <div className="form-group">
                <label>Roles</label>
                <div className="role-selection">
                  {roles.map(role => (
                    <div key={role.role_id} className="checkbox-group">
                      <label>
                        <input
                          type="checkbox"
                          checked={formData.selectedRoles.includes(role.role_id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({
                                ...formData,
                                selectedRoles: [...formData.selectedRoles, role.role_id]
                              });
                            } else {
                              setFormData({
                                ...formData,
                                selectedRoles: formData.selectedRoles.filter(id => id !== role.role_id)
                              });
                            }
                          }}
                        />
                        {role.role_name}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Subscription Tier Section (edit only) */}
              {editingUser && (
                <>
                <div className="form-group" style={{ 
                  background: '#f8f9fa', 
                  border: '1px solid #e9ecef', 
                  borderRadius: '8px', 
                  padding: '1rem',
                  marginTop: '0.5rem'
                }}>
                  <label style={{ fontWeight: '600', marginBottom: '0.75rem', display: 'block' }}>
                    Subscription Tier
                  </label>
                  <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <select
                      value={editingUser.subscription_tier || 'free'}
                      onChange={(e) => setEditingUser(prev => ({ ...prev, subscription_tier: e.target.value }))}
                      style={{
                        padding: '8px 12px',
                        borderRadius: '6px',
                        border: '1px solid #e9ecef',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        background: 'white'
                      }}
                    >
                      <option value="free">Free</option>
                      <option value="pro">Pro ($1.99/mo)</option>
                      <option value="business">Business</option>
                    </select>
                    <span style={{ fontSize: '13px', color: '#666' }}>
                      Replies this month: <strong>{editingUser.monthly_reply_count || 0}</strong>
                      {(editingUser.subscription_tier || 'free') === 'free' && ' / 10'}
                    </span>
                  </div>
                  <div style={{ marginTop: '0.75rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '14px', color: '#333' }}>
                      <input
                        type="checkbox"
                        checked={billingExempt}
                        onChange={(e) => setBillingExempt(e.target.checked)}
                        style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                      />
                      Complimentary (no billing)
                      <span style={{ fontSize: '12px', color: '#888', fontStyle: 'italic' }}>
                        — comp account, testing, etc.
                      </span>
                    </label>
                  </div>
                  <div style={{ marginTop: '0.5rem' }}>
                    <input
                      type="text"
                      placeholder="Note (e.g., legendary builder, internal testing, etc.)"
                      value={tierChangeNote}
                      onChange={(e) => setTierChangeNote(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '6px 10px',
                        borderRadius: '6px',
                        border: '1px solid #e9ecef',
                        fontSize: '13px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => handleChangeTier(editingUser.user_id, editingUser.subscription_tier || 'free', editingUser.username, billingExempt)}
                    style={{
                      marginTop: '0.75rem',
                      padding: '6px 16px',
                      background: '#2563eb',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Apply Tier Change
                  </button>
                </div>

                {/* Industry VIP Section */}
                <div style={{
                  background: industryVip ? '#fffbeb' : '#f8f9fa',
                  border: `1px solid ${industryVip ? '#f59e0b' : '#e9ecef'}`,
                  borderRadius: '8px',
                  padding: '1rem',
                  marginTop: '0.75rem'
                }}>
                  <label style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block', color: industryVip ? '#7c2d12' : '#333' }}>
                    ⭐ Industry VIP Badge
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '14px', color: '#333' }}>
                    <input
                      type="checkbox"
                      checked={industryVip}
                      onChange={(e) => {
                        setIndustryVip(e.target.checked);
                        handleToggleVIP(editingUser.user_id, e.target.checked, editingUser.username);
                      }}
                      style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                    />
                    Industry VIP
                    <span style={{ fontSize: '12px', color: '#888', fontStyle: 'italic' }}>
                      — amp builders, YouTube reviewers, industry luminaries
                    </span>
                  </label>
                </div>

                {/* Password Expiry Section */}
                <div style={{
                  background: passwordNeverExpires ? '#f0fdf4' : '#f8f9fa',
                  border: `1px solid ${passwordNeverExpires ? '#86efac' : '#e9ecef'}`,
                  borderRadius: '8px',
                  padding: '1rem',
                  marginTop: '0.75rem'
                }}>
                  <label style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block', color: passwordNeverExpires ? '#166534' : '#333' }}>
                    🔑 Password Expiry
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '14px', color: '#333' }}>
                    <input
                      type="checkbox"
                      checked={passwordNeverExpires}
                      onChange={(e) => setPasswordNeverExpires(e.target.checked)}
                      style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                    />
                    Never expire password
                    <span style={{ fontSize: '12px', color: '#888', fontStyle: 'italic' }}>
                      — skips the max-age policy check at login
                    </span>
                  </label>
                </div>
                </>
              )}

              {/* Notification & App Preferences */}
              {editingUser && (
                <div style={{
                  background: '#f8faff',
                  border: '1px solid #c7d7f5',
                  borderRadius: '8px',
                  padding: '1rem',
                  marginTop: '0.75rem'
                }}>
                  <label style={{ fontWeight: '600', marginBottom: '0.75rem', display: 'block', color: '#1e3a8a', fontSize: '14px' }}>
                    🔔 Notification &amp; App Preferences
                  </label>

                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '0.4rem' }}>
                      Email Digest Frequency
                    </label>
                    {[{ value: 'none', label: 'None', desc: 'In-app notifications only' },
                      { value: 'daily', label: 'Daily', desc: 'One summary email each morning' },
                      { value: 'weekly', label: 'Weekly', desc: 'One summary email every Monday' }
                    ].map(opt => (
                      <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.35rem', cursor: 'pointer', fontSize: '13px', color: '#374151' }}>
                        <input
                          type="radio"
                          name="emailDigestFrequency"
                          value={opt.value}
                          checked={emailDigestFrequency === opt.value}
                          onChange={() => setEmailDigestFrequency(opt.value)}
                        />
                        <strong>{opt.label}</strong>
                        <span style={{ color: '#6b7280', fontStyle: 'italic' }}>— {opt.desc}</span>
                      </label>
                    ))}
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '0.4rem' }}>
                      Default Landing Page
                    </label>
                    <select
                      value={adminLandingPage}
                      onChange={(e) => setAdminLandingPage(e.target.value)}
                      style={{ fontSize: '13px', padding: '0.35rem 0.5rem', borderRadius: '4px', border: '1px solid #d1d5db', width: '100%' }}
                    >
                      <option value="/amplifier_search">Amplifier Search</option>
                      <option value="/news-feed">News Feed</option>
                      <option value="/discussions">Discussions</option>
                      <option value="/value">Valuation</option>
                    </select>
                  </div>
                </div>
              )}

              {/* Watched Items */}
              {editingUser && (
                <div style={{
                  background: '#f8faff',
                  border: '1px solid #c7d7f5',
                  borderRadius: '8px',
                  padding: '1rem',
                  marginTop: '0.75rem'
                }}>
                  <label style={{ fontWeight: '600', marginBottom: '0.75rem', display: 'block', color: '#1e3a8a', fontSize: '14px' }}>
                    👁️ Watched Items
                    {!watchedItemsLoading && (
                      <span style={{ fontWeight: '400', color: '#6b7280', marginLeft: '0.5rem' }}>
                        ({watchedItems.length})
                      </span>
                    )}
                  </label>
                  {watchedItemsError && (
                    <p style={{ fontSize: '12px', color: '#dc2626', margin: '0 0 0.5rem' }}>{watchedItemsError}</p>
                  )}
                  {watchedItemsLoading ? (
                    <p style={{ fontSize: '13px', color: '#6b7280', margin: 0 }}>Loading…</p>
                  ) : watchedItems.length === 0 ? (
                    <p style={{ fontSize: '13px', color: '#6b7280', margin: 0, fontStyle: 'italic' }}>No watched items.</p>
                  ) : (
                    (() => {
                      const groups = watchedItems.reduce((acc, item) => {
                        const key = item.type_label || item.entity_type;
                        if (!acc[key]) acc[key] = [];
                        acc[key].push(item);
                        return acc;
                      }, {});
                      return Object.entries(groups).map(([groupLabel, items]) => (
                        <div key={groupLabel} style={{ marginBottom: '0.6rem' }}>
                          <div style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#4b5563', marginBottom: '0.3rem' }}>
                            {groupLabel}
                          </div>
                          {items.map(item => (
                            <div key={item.subscription_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '13px', color: '#374151', padding: '0.25rem 0', borderBottom: '1px solid #e5e7eb' }}>
                              <span>{item.label || `${item.entity_type} #${item.entity_id}`}</span>
                              <button
                                type="button"
                                onClick={() => handleRemoveWatch(item.subscription_id)}
                                title="Remove watch"
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: '16px', lineHeight: 1, padding: '0 0.25rem', flexShrink: 0 }}
                                onMouseOver={e => e.currentTarget.style.color = '#dc2626'}
                                onMouseOut={e => e.currentTarget.style.color = '#9ca3af'}
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      ));
                    })()
                  )}
                </div>
              )}

              <div className="form-actions">
                <button type="submit" className="save-button">
                  {editingUser ? 'Update User' : 'Create User'}
                </button>
                <button 
                  type="button" 
                  className="cancel-button"
                  onClick={() => setShowForm(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showRoleForm && selectedUserForRoles && (
        <div className="modal-overlay">
          <div className="modal role-modal">
            <h2>Manage Roles for {selectedUserForRoles.username}</h2>
            
            {error && <div className="error-message">{error}</div>}
            
            <div className="role-management-content">
              <div className="current-roles-section">
                <h3>Current Roles</h3>
                <div className="current-roles">
                  {userRoles.length > 0 ? (
                    userRoles.map(userRole => (
                      <div key={userRole.user_role_id} className="role-item">
                        <span className="role-name">{userRole.role_name}</span>
                        <span className="role-description">{userRole.description}</span>
                        <span className="assigned-date">
                          Assigned: {new Date(userRole.assigned_at).toLocaleDateString()}
                        </span>
                        {userRole.assigned_by_username && (
                          <span className="assigned-by">
                            By: {userRole.assigned_by_username}
                          </span>
                        )}
                        <button 
                          className="remove-role-button"
                          onClick={() => handleRemoveRole(userRole.role_id)}
                        >
                          Remove
                        </button>
                      </div>
                    ))
                  ) : (
                    <p className="no-roles">No roles assigned</p>
                  )}
                </div>
              </div>

              <div className="available-roles-section">
                <h3>Available Roles</h3>
                <div className="available-roles">
                  {roles
                    .filter(role => !userRoles.some(ur => ur.role_id === role.role_id))
                    .map(role => (
                      <div key={role.role_id} className="available-role-item">
                        <span className="role-name">{role.role_name}</span>
                        <span className="role-description">{role.description}</span>
                        <span className="user-count">{role.user_count} users</span>
                        <button 
                          className="assign-role-button"
                          onClick={() => handleAssignRole(role.role_id)}
                        >
                          Assign
                        </button>
                      </div>
                    ))
                  }
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button 
                type="button"
                className="cancel-button"
                onClick={() => {
                  setShowRoleForm(false);
                  setSelectedUserForRoles(null);
                  setUserRoles([]);
                  setError('');
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagementScreen;
